/**
 * Light distribution curve (LDC) pipeline for non-Taman photometric files.
 * Detects the file format, delegates parsing to `ies.ts` (IES LM-63) or `ldt.ts` (EULUMDAT)
 * and builds the full candela grid and wireframe mesh from the shared parsed structure.
 * Unpack/mesh logic ported from taman-ml/taman/util/ies/ (unpack, model, mesh).
 */
import { isIesText, parseIES } from './ies';
import { LdcGrid, LdcParsed } from './ldcTypes';
import { parseLDT } from './ldt';

const TWO_PI = 2 * Math.PI;

/** Parse LDC text, auto-detecting IES vs LDT by content. */
function parseLdc(text: string): LdcParsed {
  return isIesText(text) ? parseIES(text) : parseLDT(text);
}

function normalizeAzimuth(phi: number): number {
  return ((phi % TWO_PI) + TWO_PI) % TWO_PI;
}

function reduceAzimuthQuadrant(phi: number): number {
  phi = normalizeAzimuth(phi);
  if (phi <= Math.PI / 2) return phi;
  if (phi <= Math.PI) return Math.PI - phi;
  if (phi <= 1.5 * Math.PI) return phi - Math.PI;
  return TWO_PI - phi;
}

function reduceAzimuthHalf(phi: number): number {
  phi = normalizeAzimuth(phi);
  return phi <= Math.PI ? phi : TWO_PI - phi;
}

/** Linearly interpolate candela columns over the stored horizontal angles for each reduced azimuth. */
function interpolatePlanes(
  storedAnglesRad: number[],
  C: number[][],
  nV: number,
  azimuth: number[],
  reduce: (phi: number) => number,
): number[][] {
  const a = storedAnglesRad;
  return azimuth.map((phi) => {
    const ar = reduce(phi);
    return Array.from({ length: nV }, (_, iv) => {
      const col = C.map((r) => r[iv] ?? 0);
      let v = col[0];
      for (let j = 0; j < a.length - 1; j++) {
        if (ar >= a[j]! && ar <= a[j + 1]!) {
          const t = (ar - a[j]!) / (a[j + 1]! - a[j]! || 1e-9);
          v = col[j]! + t * (col[j + 1]! - col[j]!);
          break;
        }
      }
      return v;
    });
  });
}

/**
 * Expand symmetric candela table to full azimuth x elevation grid (radians).
 * Returns azimuth_rad, elevation_rad, candela 2D (n_azimuth, n_elevation).
 */
function unpackToFullGrid(parsed: LdcParsed): LdcGrid {
  const { verticalAngles, horizontalAngles, I, candelaMultiplier } = parsed;
  const nH = horizontalAngles.length;
  const nV = verticalAngles.length;
  const mult = candelaMultiplier || 1;
  const C = I.map((row) => row.map((v) => v * mult));
  const hDeg = horizontalAngles;
  const maxH = nH > 0 ? hDeg[nH - 1] : 0;

  // Elevation: 0° = nadir, 180° = zenith -> [0, π]
  const elevation = verticalAngles.map((d) => Math.max(0, Math.min(Math.PI, (d * Math.PI) / 180)));

  let azimuth: number[];
  let candela: number[][];

  if (maxH <= 0) {
    // Rotationally symmetric: replicate the mean column around the full circle
    const nAz = Math.max(nH, 360);
    azimuth = Array.from({ length: nAz }, (_, i) => (TWO_PI * i) / nAz);
    const meanCol =
      nV > 0
        ? Array.from({ length: nV }, (_, iv) => C.reduce((s, r) => s + (r[iv] ?? 0), 0) / Math.max(1, C.length))
        : [0];
    candela = azimuth.map(() => meanCol);
  } else if (maxH <= 90 + 1e-6) {
    // Quadrant symmetry (0-90)
    const a = hDeg.map((d) => (d * Math.PI) / 180);
    const nAz = Math.max(nH * 4, 360);
    azimuth = Array.from({ length: nAz }, (_, i) => (TWO_PI * i) / nAz);
    candela = interpolatePlanes(a, C, nV, azimuth, reduceAzimuthQuadrant);
  } else if (maxH <= 180 + 1e-6) {
    // Bilateral symmetry (0-180)
    const a = hDeg.map((d) => (d * Math.PI) / 180);
    const nAz = Math.max(nH * 2, 360);
    azimuth = Array.from({ length: nAz }, (_, i) => (TWO_PI * i) / nAz);
    candela = interpolatePlanes(a, C, nV, azimuth, reduceAzimuthHalf);
  } else {
    // Full sweep (0-360)
    azimuth = hDeg.map((d) => ((d * Math.PI) / 180) % TWO_PI);
    candela = C.map((r) => [...r]);
    if (azimuth.length > 1 && hDeg[hDeg.length - 1]! >= 360 - 1e-6) {
      azimuth = azimuth.slice(0, -1);
      candela = candela.slice(0, -1);
    }
  }

  // Sort by azimuth
  const order = azimuth.map((_, i) => i).sort((a, b) => azimuth[a]! - azimuth[b]!);
  azimuth = order.map((i) => azimuth[i]!);
  candela = order.map((i) => candela[i]!);

  return { azimuth, elevation, candela };
}

/**
 * Build wireframe mesh from the full grid: spherical (elevation, azimuth) * C -> x,y,z,
 * then scale and rotate to match Taman convention (scale 0.001, rotate Z by π/2).
 * Returns vertices and triangles for OBJ export.
 */
function gridToMesh(grid: LdcGrid): { vertices: [number, number, number][]; triangles: [number, number, number][] } {
  const { azimuth, elevation, candela } = grid;
  const nAz = azimuth.length;
  const nEl = elevation.length;

  // Python model: ah = candela[:,:,0] = vertical (elevation), av = candela[:,:,1] = horizontal (azimuth).
  // x = C*cos(av)*sin(ah), y = C*sin(av)*sin(ah), z = -C*cos(ah). So ah = elevation, av = azimuth.
  const vertices: [number, number, number][] = [];
  // Match Python flattening order from shape (n_rows=n_elevation, n_cols=n_azimuth):
  // row-major over elevation first, azimuth second.
  for (let iEl = 0; iEl < nEl; iEl++) {
    const ah = elevation[iEl]!;
    for (let iAz = 0; iAz < nAz; iAz++) {
      const av = azimuth[iAz]!;
      const C = candela[iAz]![iEl] ?? 0;
      let x = C * Math.cos(av) * Math.sin(ah);
      let y = C * Math.sin(av) * Math.sin(ah);
      let z = -C * Math.cos(ah);
      // Scale 0.001
      x *= 0.001;
      y *= 0.001;
      z *= 0.001;
      // Rotate Z by π/2: (x,y) -> (-y, x)
      vertices.push([-y, x, z]);
    }
  }

  // Triangles: elevation = rows, azimuth = cols; azimuth wraps
  const nRows = nEl;
  const nCols = nAz;
  const idx = (row: number, col: number) => row * nCols + (col % nCols);
  const triangles: [number, number, number][] = [];
  for (let row = 0; row < nRows - 1; row++) {
    for (let col = 0; col < nCols; col++) {
      const colNext = (col + 1) % nCols;
      const v00 = idx(row, col);
      const v10 = idx(row + 1, col);
      const v01 = idx(row, colNext);
      const v11 = idx(row + 1, colNext);
      triangles.push([v00, v10, v01]);
      triangles.push([v10, v11, v01]);
    }
  }

  return { vertices, triangles };
}

/**
 * Export mesh to OBJ file content (vertices + faces).
 */
function meshToObj(vertices: [number, number, number][], triangles: [number, number, number][]): string {
  const lines: string[] = ['o ies_wireframe'];
  for (const [x, y, z] of vertices) {
    lines.push(`v ${x} ${y} ${z}`);
  }
  for (const [a, b, c] of triangles) {
    lines.push(`f ${a + 1} ${b + 1} ${c + 1}`);
  }
  return lines.join('\n');
}

/**
 * Parse LDC file text (IES or LDT, auto-detected) and return both the OBJ
 * wireframe string and the unpacked candela grid (for GPU-side real-time rendering).
 */
export function ldcTextToWireframeAndGrid(text: string): { obj: string; grid: LdcGrid } {
  const grid = unpackToFullGrid(parseLdc(text));
  const { vertices, triangles } = gridToMesh(grid);
  return { obj: meshToObj(vertices, triangles), grid };
}

/**
 * Decode a file's raw bytes to string (UTF-8 with windows-1252 fallback).
 */
function decodeBytes(bytes: Uint8Array): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252', { fatal: true }).decode(bytes);
  }
}

/**
 * Fetch an LDC file (IES or LDT/EULUMDAT, auto-detected by content) from URL
 * and return both the wireframe OBJ string and the unpacked candela grid.
 * Used by the real-time WebGPU renderer so we don't have to re-fetch the file separately.
 */
export async function fetchLdcAndBuildWireframeWithGrid(url: string): Promise<{ obj: string; grid: LdcGrid }> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`LDC fetch failed: ${response.status}`);
  const buffer = await response.arrayBuffer();
  return ldcTextToWireframeAndGrid(decodeBytes(new Uint8Array(buffer)));
}

/**
 * Create a blob URL for an OBJ string (for use as wireframeUrl).
 */
export function createObjBlobUrl(objContent: string): string {
  const blob = new Blob([objContent], { type: 'text/plain' });
  return URL.createObjectURL(blob);
}
